import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';

const API_URL = process.env.REACT_APP_API_URL || 'https://videoreel-api-habwcma9cygwhphm.westus-01.azurewebsites.net';
const PUBLISHER_JWT_KEY = 'publisher_jwt';
const getToken = () => localStorage.getItem(PUBLISHER_JWT_KEY) || '';

// ─── Styled components ────────────────────────────────────────────────────────

const Wrap = styled.div`color: white;`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 28px;
  flex-wrap: wrap;
  gap: 12px;
`;

const Title = styled.h1`
  font-size: 1.35rem;
  font-weight: 700;
  margin: 0;
  background: linear-gradient(90deg, #fff, rgba(255,255,255,0.7));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const InviteBtn = styled.button`
  background: #ff4757;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 0.88rem;
  font-weight: 600;
  padding: 10px 20px;
  cursor: pointer;
  &:hover { background: #e0404f; }
`;

const Section = styled.div`
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
`;

const SectionTitle = styled.div`
  font-size: 0.78rem;
  font-weight: 600;
  color: rgba(255,255,255,0.35);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 18px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
`;

const FilterRow = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 18px;
  flex-wrap: wrap;
`;

const Select = styled.select`
  background: #10101c;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  color: rgba(255,255,255,0.7);
  font-size: 0.85rem;
  padding: 8px 12px;
  outline: none;
  cursor: pointer;
  &:focus { border-color: #ff4757; }
`;

const SubTable = styled.table`width: 100%; border-collapse: collapse;`;

const Th = styled.th`
  text-align: left;
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(255,255,255,0.3);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 0 0 10px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
`;

// Design 051: sortable header control — a real <button> so it's keyboard-accessible.
const SortBtn = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  color: inherit;
  text-transform: inherit;
  letter-spacing: inherit;
  cursor: pointer;
  &:hover { color: rgba(255,255,255,0.6); }
`;

const Td = styled.td`
  padding: 12px 0;
  font-size: 0.88rem;
  border-bottom: 1px solid rgba(255,255,255,0.04);
  color: rgba(255,255,255,0.8);
  vertical-align: middle;
`;

const StatusPill = styled.span`
  font-size: 0.7rem;
  font-weight: 700;
  padding: 2px 9px;
  border-radius: 20px;
  background: ${p => p.$active ? 'rgba(46,204,113,0.15)' : 'rgba(255,200,0,0.1)'};
  color: ${p => p.$active ? '#2ecc71' : '#f0c020'};
  border: 1px solid ${p => p.$active ? 'rgba(46,204,113,0.3)' : 'rgba(255,200,0,0.25)'};
`;

// Manual Status (design 033 add-on) — publisher-set label, no behavior. Amber when paused.
const ManualStatusSelect = styled.select`
  background: ${p => p.$paused ? 'rgba(240,160,32,0.12)' : '#10101c'};
  border: 1px solid ${p => p.$paused ? 'rgba(240,160,32,0.45)' : 'rgba(255,255,255,0.12)'};
  border-radius: 7px;
  color: ${p => p.$paused ? '#f0b020' : 'rgba(255,255,255,0.75)'};
  font-size: 0.78rem;
  font-weight: 600;
  padding: 4px 8px;
  outline: none;
  cursor: pointer;
  &:focus { border-color: #ff4757; }
  &:disabled { opacity: 0.5; cursor: default; }
`;

const RemoveBtn = styled.button`
  background: none;
  border: 1px solid rgba(255,71,87,0.25);
  border-radius: 7px;
  color: rgba(255,71,87,0.6);
  font-size: 0.75rem;
  padding: 3px 10px;
  cursor: pointer;
  &:hover { border-color: #ff4757; color: #ff4757; }
`;

const TopupBtn = styled.button`
  background: none;
  border: 1px solid rgba(46,204,113,0.25);
  border-radius: 7px;
  color: rgba(46,204,113,0.6);
  font-size: 0.75rem;
  padding: 3px 10px;
  cursor: pointer;
  margin-right: 6px;
  &:hover { border-color: #2ecc71; color: #2ecc71; }
`;

const MailBtn = styled.button`
  background: none;
  border: 1px solid rgba(110,120,255,0.3);
  border-radius: 7px;
  color: rgba(150,160,255,0.7);
  font-size: 0.75rem;
  padding: 3px 10px;
  cursor: pointer;
  margin-right: 6px;
  &:hover:not(:disabled) { border-color: #8a93ff; color: #aab2ff; }
  &:disabled { opacity: 0.5; cursor: default; }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalBox = styled.div`
  background: #13131f;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 16px;
  padding: 28px;
  min-width: 320px;
  max-width: 420px;
  width: 90%;
`;

const ModalTitle = styled.h3`
  margin: 0 0 6px;
  font-size: 1rem;
  font-weight: 700;
  color: #fff;
`;

const ModalSub = styled.p`
  margin: 0 0 18px;
  font-size: 0.82rem;
  color: rgba(255,255,255,0.35);
`;

const ModalInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  background: #0c0c1c;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 10px;
  color: white;
  font-size: 1rem;
  padding: 10px 14px;
  outline: none;
  margin-bottom: 14px;
  &:focus { border-color: #2ecc71; }
  &::placeholder { color: rgba(255,255,255,0.2); }
`;

const ModalBtns = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const BalancePill = styled.span`
  font-size: 0.78rem;
  font-weight: 700;
  padding: 2px 9px;
  border-radius: 20px;
  background: ${p => p.$zero ? 'rgba(255,71,87,0.1)' : p.$low ? 'rgba(240,160,32,0.1)' : 'rgba(46,204,113,0.08)'};
  color: ${p => p.$zero ? '#ff4757' : p.$low ? '#f0a020' : 'rgba(255,255,255,0.5)'};
  border: 1px solid ${p => p.$zero ? 'rgba(255,71,87,0.25)' : p.$low ? 'rgba(240,160,32,0.2)' : 'rgba(255,255,255,0.08)'};
`;

const WelcomePill = styled.span`
  font-size: 0.7rem;
  font-weight: 700;
  padding: 2px 9px;
  border-radius: 20px;
  background: ${p => p.$sent ? 'rgba(46,204,113,0.15)' : 'rgba(240,160,32,0.1)'};
  color: ${p => p.$sent ? '#2ecc71' : '#f0a020'};
  border: 1px solid ${p => p.$sent ? 'rgba(46,204,113,0.3)' : 'rgba(240,160,32,0.25)'};
`;

const Empty = styled.p`color: rgba(255,255,255,0.25); font-size: 0.88rem; margin: 0;`;
const ErrMsg = styled.p`color: #ff6b7a; font-size: 0.82rem; margin: 10px 0 0;`;
const OkMsg = styled.p`color: #2ecc71; font-size: 0.82rem; margin: 10px 0 0;`;

const HistoryBtn = styled.button`
  background: none;
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 7px;
  color: rgba(255,255,255,0.4);
  font-size: 0.75rem;
  padding: 3px 10px;
  cursor: pointer;
  margin-right: 6px;
  &:hover { border-color: rgba(255,255,255,0.3); color: rgba(255,255,255,0.7); }
`;

// Notes (Design 033) — available to any team member, amber "has-note" cue.
const NotesBtn = styled.button`
  background: none;
  border: 1px solid ${p => p.$hasNote ? 'rgba(240,200,80,0.4)' : 'rgba(255,255,255,0.12)'};
  border-radius: 7px;
  color: ${p => p.$hasNote ? 'rgba(240,200,80,0.85)' : 'rgba(255,255,255,0.4)'};
  font-size: 0.75rem;
  padding: 3px 10px;
  cursor: pointer;
  margin-right: 6px;
  &:hover {
    border-color: ${p => p.$hasNote ? 'rgba(240,200,80,0.7)' : 'rgba(255,255,255,0.3)'};
    color: ${p => p.$hasNote ? '#f0c850' : 'rgba(255,255,255,0.7)'};
  }
`;

const NotesTextarea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 80px;
  resize: vertical;
  background: #0c0c1c;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 10px;
  color: rgba(255,255,255,0.85);
  font-size: 0.85rem;
  font-family: inherit;
  line-height: 1.5;
  padding: 10px 12px;
  outline: none;
  &:focus { border-color: rgba(240,200,80,0.5); }
  &::placeholder { color: rgba(255,255,255,0.2); }
`;

const NotesCounter = styled.div`
  font-size: 0.72rem;
  color: rgba(255,255,255,0.3);
  margin: 6px 0 10px;
  text-align: right;
`;

const NotesBtns = styled.div`
  display: flex;
  gap: 8px;
`;

const LedgerRow = styled.tr`
  background: rgba(255,255,255,0.015);
`;

const LedgerCell = styled.td`
  padding: 0 0 16px;
  border-bottom: 1px solid rgba(255,255,255,0.04);
`;

const LedgerWrap = styled.div`
  padding: 12px 0 0 0;
`;

const LedgerTitle = styled.div`
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(255,255,255,0.25);
  text-transform: uppercase;
  letter-spacing: 0.07em;
  margin-bottom: 10px;
`;

const LedgerTable = styled.table`width: 100%; border-collapse: collapse;`;

const LTh = styled.th`
  text-align: left;
  font-size: 0.68rem;
  font-weight: 600;
  color: rgba(255,255,255,0.2);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 0 12px 8px 0;
  border-bottom: 1px solid rgba(255,255,255,0.05);
`;

const LTd = styled.td`
  padding: 8px 12px 8px 0;
  font-size: 0.8rem;
  color: rgba(255,255,255,0.55);
  border-bottom: 1px solid rgba(255,255,255,0.03);
  vertical-align: middle;
`;

const DeltaPill = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 12px;
  background: ${p => p.$positive ? 'rgba(46,204,113,0.1)' : p.$neutral ? 'rgba(255,255,255,0.06)' : 'rgba(255,71,87,0.1)'};
  color: ${p => p.$positive ? '#2ecc71' : p.$neutral ? 'rgba(255,255,255,0.35)' : '#ff6b7a'};
`;

const LedgerEmpty = styled.p`
  font-size: 0.8rem;
  color: rgba(255,255,255,0.2);
  margin: 4px 0 0;
`;

const LedgerLoading = styled.p`
  font-size: 0.8rem;
  color: rgba(255,255,255,0.25);
  margin: 4px 0 0;
`;

// ─── Invite form ──────────────────────────────────────────────────────────────

const InviteRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  flex-wrap: wrap;
`;

const EmailInput = styled.input`
  flex: 1;
  min-width: 200px;
  background: #10101c;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 10px;
  color: white;
  font-size: 0.9rem;
  padding: 10px 14px;
  outline: none;
  &:focus { border-color: #ff4757; }
  &::placeholder { color: rgba(255,255,255,0.2); }
`;

const GenBtn = styled.button`
  background: #ff4757;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 0.88rem;
  font-weight: 600;
  padding: 10px 20px;
  cursor: pointer;
  white-space: nowrap;
  &:hover:not(:disabled) { background: #e0404f; }
  &:disabled { background: #333; color: #555; cursor: default; }
`;

const LinkBox = styled.div`
  margin-top: 14px;
  background: #0c0c1c;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 10px;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const LinkText = styled.code`
  flex: 1;
  font-size: 0.78rem;
  color: rgba(255,255,255,0.55);
  word-break: break-all;
  font-family: 'SF Mono', 'Fira Mono', monospace;
`;

const CopyBtn = styled.button`
  background: rgba(255,255,255,0.07);
  color: rgba(255,255,255,0.7);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 8px;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 6px 12px;
  cursor: pointer;
  white-space: nowrap;
  &:hover { background: rgba(255,255,255,0.12); color: white; }
`;

const Expiry = styled.p`font-size: 0.75rem; color: rgba(255,255,255,0.25); margin: 8px 0 0;`;

// ─── Open invite link cards ───────────────────────────────────────────────────

const OpenInviteGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 14px;
`;

const OpenInviteCard = styled.div`
  background: rgba(255,255,255,0.02);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 12px;
  padding: 16px;
`;

const CardPubName = styled.div`
  font-size: 0.88rem;
  font-weight: 600;
  color: rgba(255,255,255,0.75);
  margin-bottom: 10px;
`;

const InlineLinkBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #0c0c1c;
  border: 1px solid rgba(255,255,255,0.09);
  border-radius: 8px;
  padding: 8px 10px;
  margin-bottom: 10px;
`;

const InlineLinkText = styled.code`
  flex: 1;
  font-size: 0.72rem;
  color: rgba(255,255,255,0.45);
  word-break: break-all;
  font-family: 'SF Mono', 'Fira Mono', monospace;
`;

const SmallBtn = styled.button`
  background: ${p => p.$primary ? '#ff4757' : 'rgba(255,255,255,0.06)'};
  color: ${p => p.$primary ? 'white' : 'rgba(255,255,255,0.65)'};
  border: 1px solid ${p => p.$primary ? 'transparent' : 'rgba(255,255,255,0.1)'};
  border-radius: 8px;
  font-size: 0.76rem;
  font-weight: 600;
  padding: 6px 12px;
  cursor: pointer;
  white-space: nowrap;
  &:hover:not(:disabled) { opacity: 0.85; }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const OpenInviteBtns = styled.div`display: flex; gap: 8px; flex-wrap: wrap;`;

// ─── Component ────────────────────────────────────────────────────────────────

const PublisherSubscribers = ({ isOwner }) => {
  const [subs, setSubs] = useState([]);
  const [pubs, setPubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterPub, setFilterPub] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterRenewal, setFilterRenewal] = useState(''); // design 042: '' | 'paid' | 'renewed'
  const [lastSeenSort, setLastSeenSort] = useState(null);  // design 051: null | 'desc' | 'asc'
  const [renewingKey, setRenewingKey] = useState(null);    // `${pub}|${email}` currently renewing
  const [showInvite, setShowInvite] = useState(false);

  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePub, setInvitePub] = useState('');
  const [generating, setGenerating] = useState(false);
  const [inviteLink, setInviteLink] = useState(null);
  const [inviteExpiry, setInviteExpiry] = useState(null);
  const [copied, setCopied] = useState(false);
  const [genError, setGenError] = useState('');
  const [genOk, setGenOk] = useState('');

  // open invite links: { [pub_id]: url_or_null }
  const [openInvites, setOpenInvites] = useState({});
  const [generatingOpenInvite, setGeneratingOpenInvite] = useState(null); // pub_id being generated
  const [copiedOpen, setCopiedOpen] = useState(null); // pub_id just copied
  const [confirmRegenPubId, setConfirmRegenPubId] = useState(null); // pub_id awaiting regen confirm

  // top-up modal state
  const [topupTarget, setTopupTarget] = useState(null); // { publication_id, email, currentBalance }
  const [topupPoints, setTopupPoints] = useState('50');
  const [topupLoading, setTopupLoading] = useState(false);
  const [topupError, setTopupError] = useState('');
  const [topupOk, setTopupOk] = useState('');

  // audit trail state
  const [expandedKey, setExpandedKey] = useState(null); // "pub_id|email"
  const [ledgerCache, setLedgerCache] = useState({}); // key -> { entries, loading, error }
  const [emailLogCache, setEmailLogCache] = useState({}); // key -> { entries, loading, error } (design 043)

  // welcome-email send state, keyed by "pub_id|email"
  const [welcomeState, setWelcomeState] = useState({}); // key -> { status: 'sending'|'sent'|'error', error? }

  // notes editor state (Design 033), keyed by "pub_id|email" — independent of the ledger expand
  const [notesOpenKey, setNotesOpenKey] = useState(null);
  const [notesDraft, setNotesDraft] = useState({}); // key -> current textarea value
  const [notesState, setNotesState] = useState({}); // key -> { status: 'saving'|'saved'|'error', error? }

  // Manual Status inline-select state, keyed by "pub_id|email"
  const [manualStatusState, setManualStatusState] = useState({}); // key -> { saving?: bool, error?: string }

  const handleToggleLedger = useCallback(async (s) => {
    const key = `${s.publication_id}|${s.email}`;
    if (expandedKey === key) {
      setExpandedKey(null);
      return;
    }
    setExpandedKey(key);
    if (ledgerCache[key]) return; // already loaded (ledger + email log fetched together)
    const auth = { headers: { Authorization: `Bearer ${getToken()}` } };
    setLedgerCache(prev => ({ ...prev, [key]: { entries: [], loading: true, error: null } }));
    setEmailLogCache(prev => ({ ...prev, [key]: { entries: [], loading: true, error: null } }));
    // Points & view history
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/publisher/subscribers/${s.publication_id}/${encodeURIComponent(s.email)}/ledger`, auth);
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
        setLedgerCache(prev => ({ ...prev, [key]: { entries: data.ledger || [], loading: false, error: null } }));
      } catch (err) {
        setLedgerCache(prev => ({ ...prev, [key]: { entries: [], loading: false, error: err.message } }));
      }
    })();
    // Email history (design 043)
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/publisher/subscribers/${s.publication_id}/${encodeURIComponent(s.email)}/email-log`, auth);
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
        setEmailLogCache(prev => ({ ...prev, [key]: { entries: data.emails || [], loading: false, error: null } }));
      } catch (err) {
        setEmailLogCache(prev => ({ ...prev, [key]: { entries: [], loading: false, error: err.message } }));
      }
    })();
  }, [expandedKey, ledgerCache]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [subRes, pubRes] = await Promise.all([
        fetch(`${API_URL}/api/publisher/subscribers`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        }),
        fetch(`${API_URL}/api/publisher/publications`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        }),
      ]);
      if (subRes.ok) {
        const data = await subRes.json();
        setSubs(data.subscribers || []);
      }
      if (pubRes.ok) {
        const data = await pubRes.json();
        const loadedPubs = data.publications || [];
        setPubs(loadedPubs);
        if (!invitePub && loadedPubs.length) {
          setInvitePub(loadedPubs[0].id);
        }
        // load open invite links in parallel
        const inviteResults = await Promise.all(
          loadedPubs.map(p =>
            fetch(`${API_URL}/api/publisher/publications/${p.id}/open-invite`, {
              headers: { Authorization: `Bearer ${getToken()}` },
            }).then(r => r.ok ? r.json() : null).catch(() => null)
          )
        );
        const map = {};
        loadedPubs.forEach((p, i) => {
          map[p.id] = inviteResults[i]?.invite_url || null;
        });
        setOpenInvites(map);
      }
    } catch {
      // non-critical
    } finally {
      setLoading(false);
    }
  }, [invitePub]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleInvite = async (e) => {
    e.preventDefault();
    setGenError('');
    setGenOk('');
    setInviteLink(null);
    if (!inviteEmail || !inviteEmail.includes('@')) {
      setGenError('Enter a valid email address.');
      return;
    }
    if (!invitePub) {
      setGenError('Select a publication.');
      return;
    }
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/publisher/subscribers/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ email: inviteEmail.trim().toLowerCase(), publication_id: invitePub }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
      setInviteLink(data.invite_url);
      setInviteExpiry(data.expires_at ? new Date(data.expires_at).toLocaleDateString() : null);
      setGenOk(`Invite link created for ${inviteEmail.trim()}`);
      setInviteEmail('');
      fetchAll();
    } catch (err) {
      setGenError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    if (!inviteLink) return;
    navigator.clipboard.writeText(inviteLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const handleRemove = async (publicationId, email) => {
    if (!window.confirm(`Remove ${email} from this publication?`)) return;
    try {
      await fetch(`${API_URL}/api/publisher/subscribers/${publicationId}/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      fetchAll();
    } catch { /* non-critical */ }
  };

  const handleSendWelcome = async (s) => {
    const key = `${s.publication_id}|${s.email}`;
    if (s.welcome_email_sent_at) {
      const date = new Date(s.welcome_email_sent_at).toLocaleDateString();
      if (!window.confirm(`Re-send the welcome email to ${s.email}? It was already sent on ${date}.`)) return;
    }
    setWelcomeState(prev => ({ ...prev, [key]: { status: 'sending' } }));
    try {
      const res = await fetch(
        `${API_URL}/api/publisher/subscribers/${s.publication_id}/${encodeURIComponent(s.email)}/welcome-email`,
        { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` } }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
      setWelcomeState(prev => ({ ...prev, [key]: { status: 'sent' } }));
      setTimeout(() => {
        setWelcomeState(prev => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
        fetchAll();
      }, 4000);
    } catch (err) {
      setWelcomeState(prev => ({ ...prev, [key]: { status: 'error', error: err.message } }));
    }
  };

  const handleToggleNotes = useCallback((s) => {
    const key = `${s.publication_id}|${s.email}`;
    if (notesOpenKey === key) {
      setNotesOpenKey(null);
      return;
    }
    // seed the draft from the currently-saved note when opening
    setNotesDraft(d => ({ ...d, [key]: s.notes || '' }));
    setNotesOpenKey(key);
  }, [notesOpenKey]);

  const handleSaveNotes = useCallback(async (s) => {
    const key = `${s.publication_id}|${s.email}`;
    const draft = notesDraft[key] ?? '';
    setNotesState(prev => ({ ...prev, [key]: { status: 'saving' } }));
    try {
      const res = await fetch(
        `${API_URL}/api/publisher/subscribers/${s.publication_id}/${encodeURIComponent(s.email)}/notes`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
          body: JSON.stringify({ notes: draft }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
      setNotesState(prev => ({ ...prev, [key]: { status: 'saved' } }));
      setTimeout(() => {
        setNotesState(prev => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
        fetchAll();
      }, 2500);
    } catch (err) {
      setNotesState(prev => ({ ...prev, [key]: { status: 'error', error: err.message } }));
    }
  }, [notesDraft, fetchAll]);

  const handleManualStatusChange = async (s, newStatus) => {
    const key = `${s.publication_id}|${s.email}`;
    const prev = s.manual_status || 'active';
    if (newStatus === prev) return;
    // Optimistic: update the row locally, revert on failure.
    setSubs(list => list.map(x =>
      (x.publication_id === s.publication_id && x.email === s.email)
        ? { ...x, manual_status: newStatus } : x));
    setManualStatusState(m => ({ ...m, [key]: { saving: true } }));
    try {
      const res = await fetch(
        `${API_URL}/api/publisher/subscribers/${s.publication_id}/${encodeURIComponent(s.email)}/manual-status`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
          body: JSON.stringify({ manual_status: newStatus }),
        }
      );
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || `Error ${res.status}`); }
      setManualStatusState(m => { const n = { ...m }; delete n[key]; return n; });
    } catch (err) {
      setSubs(list => list.map(x =>
        (x.publication_id === s.publication_id && x.email === s.email)
          ? { ...x, manual_status: prev } : x));
      setManualStatusState(m => ({ ...m, [key]: { error: err.message } }));
      setTimeout(() => setManualStatusState(m => { const n = { ...m }; delete n[key]; return n; }), 4000);
    }
  };

  const handleGenerateOpenInvite = async (pubId) => {
    setGeneratingOpenInvite(pubId);
    try {
      const res = await fetch(`${API_URL}/api/publisher/publications/${pubId}/open-invite`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
      setOpenInvites(prev => ({ ...prev, [pubId]: data.invite_url }));
    } catch { /* non-critical */ } finally {
      setGeneratingOpenInvite(null);
    }
  };

  const handleCopyOpenInvite = (pubId, url) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedOpen(pubId);
      setTimeout(() => setCopiedOpen(null), 2500);
    });
  };

  const handleTopupOpen = (s) => {
    setTopupTarget({ publication_id: s.publication_id, email: s.email, currentBalance: s.points_balance ?? 30 });
    setTopupPoints('50');
    setTopupError('');
    setTopupOk('');
  };

  const handleTopupSubmit = async () => {
    const pts = parseInt(topupPoints, 10);
    if (!pts || pts <= 0 || pts > 10000) {
      setTopupError('Enter a number between 1 and 10,000.');
      return;
    }
    setTopupLoading(true);
    setTopupError('');
    setTopupOk('');
    try {
      const res = await fetch(
        `${API_URL}/api/publisher/subscribers/${topupTarget.publication_id}/${encodeURIComponent(topupTarget.email)}/topup`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
          body: JSON.stringify({ points: pts }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
      setTopupOk(`Balance updated: ${data.balance_after} points`);
      fetchAll();
      setTimeout(() => setTopupTarget(null), 1500);
    } catch (err) {
      setTopupError(err.message);
    } finally {
      setTopupLoading(false);
    }
  };

  // Design 042: one-click renew (top up + mark renewed) for a single subscriber. Amount is the
  // publication's renewal_default_points (server-authoritative — we omit amount).
  const handleRenewRow = async (s) => {
    const key = `${s.publication_id}|${s.email}`;
    setRenewingKey(key);
    try {
      const res = await fetch(`${API_URL}/api/publisher/publications/${s.publication_id}/renewals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ emails: [s.email], allow_rerenew: false }),
      });
      if (res.ok) fetchAll();
    } catch { /* transient — badge reconciles on next load */ }
    finally { setRenewingKey(null); }
  };

  const pubName = (id) => pubs.find(p => p.id === id)?.name || id;
  const pubRenewAmt = (id) => pubs.find(p => p.id === id)?.renewal_default_points ?? 25;

  const fmtLedgerDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
      + ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  };

  // Design 051: last_login_at is naive-UTC (utcnow().isoformat(), no 'Z'); without a zone
  // designator the browser would read it as LOCAL time, so append 'Z'. Returns epoch ms or null.
  const parseUtcIso = (iso) => {
    if (!iso) return null;
    const hasZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(iso);
    const t = new Date(hasZone ? iso : `${iso}Z`).getTime();
    return isNaN(t) ? null : t;
  };

  // Design 051: always an absolute, locale-unambiguous date ("20 Oct 2026") in the viewer's tz.
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const fmtLastSeen = (iso) => {
    const t = parseUtcIso(iso);
    if (t === null) return 'Never';
    const d = new Date(t);
    return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
  };

  const fmtReason = (reason, title, videoId) => {
    if (reason === 'initial') return 'Starting balance';
    if (reason === 'topup') return 'Top-up';
    if (reason === 'renewal') return 'Renewal';
    if (reason === 'video_watch') return title || videoId || 'Video watched';
    return reason || '—';
  };

  const fmtEmailType = (t) => {
    if (t === 'welcome') return 'Welcome email';
    if (t === 'balance_cleared') return 'Balance-cleared email';
    if (t === 'points_reminder') return 'Points reminder';
    if (t === 'communications') return 'Communications';
    return t || 'Email';
  };

  // Design 042 renewal derivation. "Renewed" = renewed within a rolling window (not the calendar
  // month), so a renewal done on e.g. Aug 31 still reads as renewed through September — the month
  // boundary can't drop it. Uses renewed_at (the actual date); the badge shows that real date.
  const RENEWED_WINDOW_MS = 35 * 24 * 60 * 60 * 1000;
  const isRenewed = (s) => {
    if (!s.renewed_at) return false;
    const t = new Date(s.renewed_at).getTime();
    return !isNaN(t) && (Date.now() - t) <= RENEWED_WINDOW_MS;
  };
  const renewedOn = (s) => s.renewed_at
    ? new Date(s.renewed_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : '';
  const isPaidNeedsRenewal = (s) => s.status === 'active' && (s.points_balance ?? 30) <= 0 && !isRenewed(s);

  const inPub = subs.filter(s => !filterPub || s.publication_id === filterPub);
  const paidCount = inPub.filter(isPaidNeedsRenewal).length;
  const renewedCount = inPub.filter(isRenewed).length;

  const filtered = subs.filter(s => {
    if (filterPub && s.publication_id !== filterPub) return false;
    if (filterStatus && s.status !== filterStatus) return false;
    if (filterRenewal === 'paid' && !isPaidNeedsRenewal(s)) return false;
    if (filterRenewal === 'renewed' && !isRenewed(s)) return false;
    return true;
  });

  // Design 051: tri-state Last-seen sort over a COPY of `filtered` (counts/optimistic updates
  // keep using the unsorted arrays). Never-seen + invited rows always sort last.
  const lastSeenMs = (s) => (s.status === 'active' ? parseUtcIso(s.last_login_at) : null);
  const rows = !lastSeenSort ? filtered : [...filtered].sort((a, b) => {
    const ta = lastSeenMs(a);
    const tb = lastSeenMs(b);
    if (ta !== null && tb === null) return -1;
    if (ta === null && tb !== null) return 1;
    if (ta !== null && tb !== null && ta !== tb) return lastSeenSort === 'desc' ? tb - ta : ta - tb;
    return (a.email || '').localeCompare(b.email || '')
      || (a.publication_id || '').localeCompare(b.publication_id || '');
  });
  const cycleLastSeenSort = () =>
    setLastSeenSort(cur => (cur === null ? 'desc' : cur === 'desc' ? 'asc' : null));

  return (
    <Wrap>
      <TitleRow>
        <Title>Subscribers</Title>
        {isOwner && (
          <InviteBtn onClick={() => setShowInvite(v => !v)}>
            {showInvite ? 'Hide invite form' : '+ Invite subscriber'}
          </InviteBtn>
        )}
      </TitleRow>

      {isOwner && pubs.length > 0 && (
        <Section>
          <SectionTitle>General invite links</SectionTitle>
          <p style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.35)', margin: '0 0 16px', lineHeight: 1.5 }}>
            Share a link that anyone can click to sign in and join a publication. No email required.
            Regenerating a link will immediately revoke the old one.
          </p>
          <OpenInviteGrid>
            {pubs.map(p => {
              const url = openInvites[p.id];
              const isGen = generatingOpenInvite === p.id;
              return (
                <OpenInviteCard key={p.id}>
                  <CardPubName>{p.name}</CardPubName>
                  {url ? (
                    <InlineLinkBox>
                      <InlineLinkText>{url}</InlineLinkText>
                      <SmallBtn onClick={() => handleCopyOpenInvite(p.id, url)}>
                        {copiedOpen === p.id ? 'Copied!' : 'Copy'}
                      </SmallBtn>
                    </InlineLinkBox>
                  ) : (
                    <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.2)', margin: '0 0 10px' }}>
                      No link generated yet.
                    </p>
                  )}
                  <OpenInviteBtns>
                    <SmallBtn $primary disabled={isGen} onClick={() => url ? setConfirmRegenPubId(p.id) : handleGenerateOpenInvite(p.id)}>
                      {isGen ? 'Generating…' : url ? 'Regenerate link' : 'Generate link'}
                    </SmallBtn>
                  </OpenInviteBtns>
                </OpenInviteCard>
              );
            })}
          </OpenInviteGrid>
        </Section>
      )}

      {showInvite && isOwner && (
        <Section>
          <SectionTitle>Invite a subscriber</SectionTitle>
          <p style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.35)', margin: '0 0 16px', lineHeight: 1.5 }}>
            Generate an invite link for a subscriber. They'll click the link, sign in with their account,
            and be added to the selected publication automatically.
          </p>
          <form onSubmit={handleInvite} noValidate>
            <InviteRow>
              <EmailInput
                type="email"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                placeholder="subscriber@example.com"
              />
              <Select value={invitePub} onChange={e => setInvitePub(e.target.value)}>
                {pubs.length === 0 && <option value="">No publications yet</option>}
                {pubs.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </Select>
              <GenBtn type="submit" disabled={generating || !inviteEmail || !invitePub}>
                {generating ? 'Generating…' : 'Generate invite link'}
              </GenBtn>
            </InviteRow>
            {genError && <ErrMsg>{genError}</ErrMsg>}
            {genOk && <OkMsg>{genOk}</OkMsg>}
          </form>

          {inviteLink && (
            <>
              <LinkBox>
                <LinkText>{inviteLink}</LinkText>
                <CopyBtn onClick={handleCopy}>{copied ? 'Copied!' : 'Copy link'}</CopyBtn>
              </LinkBox>
              {inviteExpiry && (
                <Expiry>Expires {inviteExpiry}. Send this link directly to the subscriber — it is single-use.</Expiry>
              )}
            </>
          )}
        </Section>
      )}

      <Section>
        <SectionTitle>Subscriber roster</SectionTitle>

        <FilterRow>
          <Select value={filterPub} onChange={e => setFilterPub(e.target.value)}>
            <option value="">All publications</option>
            {pubs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
          <Select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All statuses</option>
            <option value="invited">Invited</option>
            <option value="active">Active</option>
          </Select>
          <Select value={filterRenewal} onChange={e => setFilterRenewal(e.target.value)}>
            <option value="">All renewal states</option>
            <option value="paid">Paid down — needs renewal ({paidCount})</option>
            <option value="renewed">Recently renewed ({renewedCount})</option>
          </Select>
        </FilterRow>

        {loading ? (
          <Empty>Loading…</Empty>
        ) : filtered.length === 0 ? (
          <Empty>
            {subs.length === 0
              ? 'No subscribers yet. Invite your first subscriber to get started.'
              : 'No subscribers match the current filter.'}
          </Empty>
        ) : (
          <SubTable>
            <thead>
              <tr>
                <Th>Email</Th>
                <Th>Publication</Th>
                <Th>Status</Th>
                <Th>Manual status</Th>
                <Th>Balance</Th>
                <Th>Renewal</Th>
                <Th>Joined</Th>
                <Th aria-sort={lastSeenSort === 'desc' ? 'descending' : lastSeenSort === 'asc' ? 'ascending' : 'none'}>
                  <SortBtn type="button" onClick={cycleLastSeenSort} title="Sort by last seen">
                    Last seen{' '}
                    <span style={{ opacity: lastSeenSort ? 1 : 0.5 }}>
                      {lastSeenSort === 'desc' ? '▼' : lastSeenSort === 'asc' ? '▲' : '↕'}
                    </span>
                  </SortBtn>
                </Th>
                <Th>Welcome</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map(s => {
                const key = `${s.publication_id}|${s.email}`;
                const isExpanded = expandedKey === key;
                const ledger = ledgerCache[key];
                const emailLog = emailLogCache[key];
                const isNotesOpen = notesOpenKey === key;
                const ns = notesState[key];
                const hasNote = !!(s.notes && s.notes.trim());
                const mss = manualStatusState[key];
                const manualStatus = s.manual_status || 'active';
                const colSpan = 10;
                return (
                  <React.Fragment key={key}>
                    <tr>
                      <Td>{s.email}</Td>
                      <Td style={{ color: 'rgba(255,255,255,0.5)' }}>{pubName(s.publication_id)}</Td>
                      <Td>
                        <StatusPill $active={s.status === 'active'}>
                          {s.status === 'active' ? 'Active' : 'Invited'}
                        </StatusPill>
                      </Td>
                      <Td>
                        <ManualStatusSelect
                          value={manualStatus}
                          $paused={manualStatus === 'paused'}
                          disabled={mss?.saving}
                          onChange={e => handleManualStatusChange(s, e.target.value)}
                        >
                          <option value="active">Active</option>
                          <option value="paused">Paused</option>
                        </ManualStatusSelect>
                        {mss?.error && <ErrMsg style={{ margin: '4px 0 0', fontSize: '0.72rem' }}>{mss.error}</ErrMsg>}
                      </Td>
                      <Td>
                        {s.status === 'active' ? (
                          <BalancePill $zero={(s.points_balance ?? 30) <= 0} $low={(s.points_balance ?? 30) > 0 && (s.points_balance ?? 30) <= 5}>
                            {s.points_balance ?? 30} pts
                          </BalancePill>
                        ) : (
                          <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.8rem' }}>—</span>
                        )}
                      </Td>
                      <Td>
                        {s.status !== 'active' ? (
                          <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.8rem' }}>—</span>
                        ) : isRenewed(s) ? (
                          <WelcomePill $sent title={s.renewed_at ? new Date(s.renewed_at).toLocaleString() : ''}>
                            ✓ Renewed {renewedOn(s)}
                          </WelcomePill>
                        ) : (s.points_balance ?? 30) <= 0 ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#f0a020' }}>Paid down</span>
                            {isOwner && (
                              <TopupBtn onClick={() => handleRenewRow(s)} disabled={renewingKey === key}>
                                {renewingKey === key ? 'Renewing…' : `Renew +${pubRenewAmt(s.publication_id)}`}
                              </TopupBtn>
                            )}
                          </span>
                        ) : (
                          <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.8rem' }}>—</span>
                        )}
                      </Td>
                      <Td style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.8rem' }}>
                        {s.status === 'active'
                          ? (s.joined_at ? new Date(s.joined_at).toLocaleDateString() : '—')
                          : (s.created_at ? new Date(s.created_at).toLocaleDateString() : '—')}
                      </Td>
                      <Td style={{ fontSize: '0.8rem' }}>
                        {s.status === 'active' ? (
                          <span
                            style={{ color: s.last_login_at ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.25)' }}
                            title={parseUtcIso(s.last_login_at) !== null ? new Date(parseUtcIso(s.last_login_at)).toLocaleString() : undefined}
                          >
                            {fmtLastSeen(s.last_login_at)}
                          </span>
                        ) : (
                          <span style={{ color: 'rgba(255,255,255,0.2)' }}>—</span>
                        )}
                      </Td>
                      <Td>
                        {s.status !== 'active' ? (
                          <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.8rem' }}>—</span>
                        ) : s.welcome_email_sent_at ? (
                          <WelcomePill $sent>✓ Sent {new Date(s.welcome_email_sent_at).toLocaleDateString()}</WelcomePill>
                        ) : (
                          <WelcomePill>Not sent</WelcomePill>
                        )}
                      </Td>
                      <Td>
                        <HistoryBtn onClick={() => handleToggleLedger(s)}>
                          {isExpanded ? '▲ Hide' : '▼ History'}
                        </HistoryBtn>
                        <NotesBtn $hasNote={hasNote} onClick={() => handleToggleNotes(s)}>
                          {isNotesOpen ? '▲ Notes' : hasNote ? '📝 Note' : 'Add note'}
                        </NotesBtn>
                        {isOwner && s.status === 'active' && (
                          <TopupBtn onClick={() => handleTopupOpen(s)}>Top up</TopupBtn>
                        )}
                        {isOwner && s.status === 'active' && (() => {
                          const ws = welcomeState[key];
                          const sending = ws?.status === 'sending';
                          let label;
                          if (ws?.status === 'sending') label = 'Sending…';
                          else if (ws?.status === 'sent') label = '✓ Sent';
                          else label = s.welcome_email_sent_at ? 'Re-send welcome' : 'Send welcome';
                          return (
                            <>
                              <MailBtn disabled={sending} onClick={() => handleSendWelcome(s)}>
                                {label}
                              </MailBtn>
                              {ws?.status === 'error' && <ErrMsg>{ws.error}</ErrMsg>}
                            </>
                          );
                        })()}
                        {isOwner && (
                          <RemoveBtn onClick={() => handleRemove(s.publication_id, s.email)}>Remove</RemoveBtn>
                        )}
                      </Td>
                    </tr>
                    {isExpanded && (
                      <LedgerRow>
                        <LedgerCell colSpan={colSpan}>
                          <LedgerWrap>
                            <LedgerTitle>Points &amp; view history</LedgerTitle>
                            {ledger?.loading && <LedgerLoading>Loading…</LedgerLoading>}
                            {ledger?.error && <LedgerEmpty style={{ color: '#ff6b7a' }}>{ledger.error}</LedgerEmpty>}
                            {ledger && !ledger.loading && !ledger.error && ledger.entries.length === 0 && (
                              <LedgerEmpty>No history yet.</LedgerEmpty>
                            )}
                            {ledger && !ledger.loading && ledger.entries.length > 0 && (
                              <LedgerTable>
                                <thead>
                                  <tr>
                                    <LTh>Date &amp; time</LTh>
                                    <LTh>Event / Video</LTh>
                                    <LTh>Points</LTh>
                                    <LTh>Balance</LTh>
                                  </tr>
                                </thead>
                                <tbody>
                                  {ledger.entries.map((entry, i) => {
                                    const positive = (entry.delta ?? 0) > 0;
                                    const neutral = entry.reason === 'initial';
                                    return (
                                      <tr key={i}>
                                        <LTd style={{ whiteSpace: 'nowrap' }}>{fmtLedgerDate(entry.created_at)}</LTd>
                                        <LTd style={{ color: entry.reason === 'video_watch' ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.4)' }}>
                                          {fmtReason(entry.reason, entry.title, entry.video_id)}
                                        </LTd>
                                        <LTd>
                                          <DeltaPill $positive={positive} $neutral={neutral}>
                                            {neutral ? '—' : `${positive ? '+' : ''}${entry.delta}`}
                                          </DeltaPill>
                                        </LTd>
                                        <LTd style={{ color: 'rgba(255,255,255,0.4)' }}>
                                          {entry.balance_after ?? '—'} pts
                                        </LTd>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </LedgerTable>
                            )}
                          </LedgerWrap>
                          <LedgerWrap style={{ marginTop: 14 }}>
                            <LedgerTitle>Email history</LedgerTitle>
                            {emailLog?.loading && <LedgerLoading>Loading…</LedgerLoading>}
                            {emailLog?.error && <LedgerEmpty style={{ color: '#ff6b7a' }}>{emailLog.error}</LedgerEmpty>}
                            {emailLog && !emailLog.loading && !emailLog.error && emailLog.entries.length === 0 && (
                              <LedgerEmpty>No emails sent to this subscriber yet.</LedgerEmpty>
                            )}
                            {emailLog && !emailLog.loading && emailLog.entries.length > 0 && (
                              <LedgerTable>
                                <thead>
                                  <tr>
                                    <LTh>Date &amp; time</LTh>
                                    <LTh>Email</LTh>
                                  </tr>
                                </thead>
                                <tbody>
                                  {emailLog.entries.map((en, i) => (
                                    <tr key={i}>
                                      <LTd style={{ whiteSpace: 'nowrap' }}>{fmtLedgerDate(en.sent_at)}</LTd>
                                      <LTd style={{ color: 'rgba(255,255,255,0.7)' }}>
                                        {fmtEmailType(en.email_type)}{en.subject ? ` — ${en.subject}` : ''}
                                      </LTd>
                                    </tr>
                                  ))}
                                </tbody>
                              </LedgerTable>
                            )}
                          </LedgerWrap>
                        </LedgerCell>
                      </LedgerRow>
                    )}
                    {isNotesOpen && (
                      <LedgerRow>
                        <LedgerCell colSpan={colSpan}>
                          <LedgerWrap>
                            <LedgerTitle>Private note (visible only to your team)</LedgerTitle>
                            <NotesTextarea
                              value={notesDraft[key] ?? ''}
                              maxLength={2000}
                              placeholder="e.g. comped for launch, asked to pause emails…"
                              onChange={e => setNotesDraft(d => ({ ...d, [key]: e.target.value }))}
                            />
                            <NotesCounter>{(notesDraft[key] ?? '').length} / 2000</NotesCounter>
                            <NotesBtns>
                              <SmallBtn $primary disabled={ns?.status === 'saving'} onClick={() => handleSaveNotes(s)}>
                                {ns?.status === 'saving' ? 'Saving…' : 'Save note'}
                              </SmallBtn>
                              <SmallBtn onClick={() => setNotesOpenKey(null)}>Cancel</SmallBtn>
                            </NotesBtns>
                            {ns?.status === 'saved' && <OkMsg>✓ Saved</OkMsg>}
                            {ns?.status === 'error' && <ErrMsg>{ns.error}</ErrMsg>}
                          </LedgerWrap>
                        </LedgerCell>
                      </LedgerRow>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </SubTable>
        )}
      </Section>

      {confirmRegenPubId && (
        <ModalOverlay onClick={() => setConfirmRegenPubId(null)}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <ModalTitle>Regenerate invite link?</ModalTitle>
            <ModalSub>
              The current link for <strong style={{ color: 'rgba(255,255,255,0.7)' }}>{pubs.find(p => p.id === confirmRegenPubId)?.name}</strong> will be
              permanently revoked. Anyone who received the old link will no longer be able to use it.
            </ModalSub>
            <ModalBtns>
              <SmallBtn onClick={() => setConfirmRegenPubId(null)}>Cancel</SmallBtn>
              <SmallBtn
                style={{ background: '#ff4757', color: 'white', border: 'none' }}
                onClick={() => { handleGenerateOpenInvite(confirmRegenPubId); setConfirmRegenPubId(null); }}
              >
                Yes, regenerate
              </SmallBtn>
            </ModalBtns>
          </ModalBox>
        </ModalOverlay>
      )}

      {topupTarget && (
        <ModalOverlay onClick={() => setTopupTarget(null)}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <ModalTitle>Top up points</ModalTitle>
            <ModalSub>
              {topupTarget.email} &mdash; current balance: {topupTarget.currentBalance} pts
            </ModalSub>
            <ModalInput
              type="number"
              min="1"
              max="10000"
              value={topupPoints}
              onChange={e => setTopupPoints(e.target.value)}
              placeholder="Points to add"
              autoFocus
            />
            {topupError && <ErrMsg>{topupError}</ErrMsg>}
            {topupOk && <OkMsg>{topupOk}</OkMsg>}
            <ModalBtns>
              <SmallBtn onClick={() => setTopupTarget(null)}>Cancel</SmallBtn>
              <SmallBtn $primary disabled={topupLoading} onClick={handleTopupSubmit}>
                {topupLoading ? 'Adding…' : 'Add points'}
              </SmallBtn>
            </ModalBtns>
          </ModalBox>
        </ModalOverlay>
      )}
    </Wrap>
  );
};

export default PublisherSubscribers;
